import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Download, ShieldCheck, Heart, Minus, Plus, Search as ZoomIcon,
  Lightbulb, Zap, Thermometer, Shield, Clock, Gauge, Truck, Check,
  Star, Ruler, Settings, FileText, FileImage,
} from "lucide-react";
import PageHero from "../components/PageHero";
import ProductCard from "../components/ProductCard";
import { getProduct, getCategory, getProductsByCategory } from "../data/catalog";

const Stat = ({ label, value, unit, Icon }) => (
  <div className="border border-leduro-border bg-white p-4">
    <span className="flex items-center gap-1.5 leduro-eyebrow !text-leduro-muted">
      {Icon && <Icon className="h-3 w-3 text-leduro-orange" />}
      {label}
    </span>
    <div className="mt-2 font-heading font-bold text-leduro-navy flex items-baseline gap-1">
      <span className="text-2xl lg:text-3xl">{value}</span>
      {unit && <span className="text-xs font-mono text-leduro-muted normal-case tracking-wide">{unit}</span>}
    </div>
  </div>
);

const Trust = ({ Icon, children }) => (
  <div className="flex items-center gap-2 text-sm text-leduro-navy">
    <Icon className="h-4 w-4 text-leduro-orange shrink-0" />
    {children}
  </div>
);

const TABS = ["Technical Spec", "Description", "Applications", "Downloads", "Warranty"];

export default function ProductDetail() {
  const { id } = useParams();
  const product = getProduct(id);

  const [tab, setTab] = useState(0);
  const [activeImg, setActiveImg] = useState(0);
  const [qty, setQty] = useState(50);
  const [sent, setSent] = useState(false);
  const [wishlisted, setWishlisted] = useState(false);
  const [zoom, setZoom] = useState(false);

  if (!product) {
    return (
      <div className="leduro-container py-32 text-center" data-testid="product-not-found">
        <h1 className="text-3xl font-heading font-bold text-leduro-navy">Product not found</h1>
        <Link to="/catalogue" className="leduro-btn-primary mt-6 inline-flex">Back to catalogue</Link>
      </div>
    );
  }

  const category = getCategory(product.category);
  const related = getProductsByCategory(product.category).filter((p) => p.id !== product.id).slice(0, 4);
  const images = [product.image, product.image, product.image];

  const SPECS = [
    { Icon: Lightbulb, label: "Luminous flux", value: product.lumen },
    { Icon: Zap, label: "Rated power", value: product.watt },
    { Icon: Thermometer, label: "Colour temperature", value: product.cct },
    { Icon: Star, label: "Colour rendering", value: `Ra ≥ ${product.cri}` },
    { Icon: Lightbulb, label: "Beam angle", value: product.beamAngle },
    { Icon: Shield, label: "IP rating", value: product.ip },
    { Icon: Clock, label: "Rated lifetime", value: `${product.lifetimeHours.toLocaleString()} h` },
    { Icon: Zap, label: "Operating voltage", value: product.voltage },
    { Icon: Gauge, label: "Power factor", value: product.powerFactor },
    { Icon: Ruler, label: "Dimensions", value: product.dimensions },
    { Icon: Ruler, label: "Weight", value: product.weight },
    { Icon: ShieldCheck, label: "Material", value: product.material },
    { Icon: Thermometer, label: "Operating temp.", value: product.operatingTemp },
    { Icon: Zap, label: "Driver", value: product.driver },
    { Icon: ShieldCheck, label: "Certifications", value: product.certifications.join(" · ") },
    { Icon: ShieldCheck, label: "Warranty", value: `${product.warrantyYears}Y years` },
  ];

  return (
    <div data-testid={`product-page-${product.id}`} className="bg-white">
      <PageHero
        eyebrow={category?.name}
        title={product.name}
        subtitle={`${product.sku} · ${product.lumen} · ${product.watt} · ${product.warranty} warranty`}
        crumbs={[
          { label: "Catalogue", to: "/catalogue" },
          { label: category?.name, to: `/catalogue/${category?.slug}` },
          { label: product.name },
        ]}
      />

      {/* Main detail block */}
      <section className="bg-white py-10 md:py-16">
        <div className="leduro-container grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          {/* LEFT — gallery */}
          <div className="lg:col-span-7 grid grid-cols-[80px_1fr] gap-4">
            <div className="flex flex-col gap-3" data-testid="gallery-thumbs">
              {images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImg(i)}
                  data-testid={`thumb-${i}`}
                  className={`aspect-square bg-leduro-surface border transition-colors overflow-hidden ${
                    activeImg === i ? "border-leduro-orange" : "border-leduro-border hover:border-leduro-navy"
                  }`}
                >
                  <img src={img} alt={`${product.name} angle ${i + 1}`} className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
            <div className="relative bg-leduro-surface border border-leduro-border overflow-hidden">
              <div className="aspect-[4/3]">
                <img
                  src={images[activeImg]}
                  alt={product.name}
                  className={`h-full w-full object-cover transition-transform duration-500 ${zoom ? "scale-150 cursor-zoom-out" : "cursor-zoom-in"}`}
                  onClick={() => setZoom((v) => !v)}
                />
              </div>
              {/* Warranty badge overlay */}
              <span className="absolute top-4 left-4 bg-leduro-orange text-white text-[11px] font-bold tracking-[0.18em] uppercase px-3 py-1.5">
                {product.warrantyYears}Y year warranty
              </span>
              <button
                onClick={() => setZoom((v) => !v)}
                data-testid="zoom-btn"
                className="absolute bottom-3 right-3 h-9 w-9 grid place-items-center bg-white border border-leduro-border text-leduro-navy hover:bg-leduro-navy hover:text-white transition-colors"
                aria-label="Zoom image"
              >
                <ZoomIcon className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* RIGHT — info + quote */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="inline-flex items-center font-mono text-sm text-leduro-navy">
                SKU: <span className="ml-1.5 font-bold">{product.sku}</span>
              </span>
              <span className="bg-amber-200/80 text-leduro-navy text-[10px] font-bold tracking-[0.18em] uppercase px-2.5 py-1">
                {product.warrantyYears}Y year warranty
              </span>
            </div>
            <p className="mt-2 inline-flex items-center gap-2 text-sm text-leduro-navy">
              <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse-glow" />
              In stock · {product.stockQty} pcs available
            </p>

            <h2 className="mt-3 font-heading font-bold text-leduro-navy text-3xl lg:text-4xl tracking-tight">
              {product.name}
            </h2>
            <p className="mt-3 text-sm text-leduro-muted leading-relaxed">{product.description}</p>

            {/* Stat grid 2x3 */}
            <div className="mt-6 grid grid-cols-3 gap-px bg-leduro-border border border-leduro-border" data-testid="stats-grid">
              <Stat Icon={Lightbulb} label="Lumens" value={product.lumenValue.toLocaleString()} unit="lm" />
              <Stat Icon={Zap} label="Power" value={product.wattValue} unit="W" />
              <Stat Icon={Thermometer} label="CCT" value={product.cctValue} unit="K" />
              <Stat Icon={Shield} label="Protection" value={product.ip} />
              <Stat Icon={Clock} label="Lifetime" value={`${(product.lifetimeHours / 1000)}\u00A0000`} unit="h" />
              <Stat Icon={Gauge} label="Efficacy" value={product.efficacy} unit="lm/W" />
            </div>

            {/* B2B pricing notice */}
            <div className="mt-6 bg-leduro-surface border-l-4 border-leduro-orange p-4 flex items-start gap-3" data-testid="b2b-pricing-notice">
              <div className="h-9 w-9 grid place-items-center bg-leduro-navy text-white shrink-0">
                <Shield className="h-4 w-4" />
              </div>
              <div className="text-sm">
                <p className="font-bold text-leduro-navy">B2B pricing locked</p>
                <p className="text-leduro-muted leading-relaxed mt-1">
                  <Link to="/login" className="text-leduro-navy font-semibold hover:text-leduro-orange">Sign in</Link>{" "}
                  to your partner account to view tiered pricing — or{" "}
                  <a href="#quote" className="text-leduro-orange font-semibold underline underline-offset-2">request a quote</a>{" "}
                  for unit / case / pallet rates.
                </p>
              </div>
            </div>

            {/* Qty + ADD TO QUOTE + wishlist */}
            <div className="mt-5 flex items-stretch gap-2" id="quote">
              <div className="inline-flex items-stretch border border-leduro-border" data-testid="qty-stepper">
                <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="h-12 w-11 grid place-items-center text-leduro-navy hover:bg-leduro-surface" aria-label="Decrease">
                  <Minus className="h-4 w-4" />
                </button>
                <input
                  value={qty}
                  onChange={(e) => setQty(Math.max(1, parseInt(e.target.value) || 1))}
                  data-testid="qty-input"
                  className="w-16 text-center font-bold text-leduro-navy bg-white focus:outline-none"
                />
                <button onClick={() => setQty((q) => q + 1)} className="h-12 w-11 grid place-items-center text-leduro-navy hover:bg-leduro-surface" aria-label="Increase">
                  <Plus className="h-4 w-4" />
                </button>
              </div>

              <button
                onClick={() => setSent(true)}
                data-testid="add-to-quote"
                className="flex-1 leduro-btn-primary justify-center text-xs"
              >
                <Plus className="h-4 w-4" />
                {sent ? "Added to quote request" : "Add to quote request"}
              </button>

              <button
                onClick={() => setWishlisted((v) => !v)}
                data-testid="wishlist-btn"
                aria-label="Save to wishlist"
                className={`h-12 w-12 grid place-items-center border transition-colors ${
                  wishlisted ? "bg-leduro-navy border-leduro-navy text-leduro-orange" : "border-leduro-border text-leduro-navy hover:bg-leduro-navy hover:text-white"
                }`}
              >
                <Heart className={`h-4 w-4 ${wishlisted ? "fill-current" : ""}`} />
              </button>
            </div>

            {sent && (
              <p data-testid="quote-success" className="mt-3 text-xs text-leduro-orange font-semibold flex items-center gap-1.5">
                <Check className="h-3.5 w-3.5" /> Added — review in your quote drawer at checkout.
              </p>
            )}

            {/* Downloads */}
            <div className="mt-4 grid grid-cols-2 gap-2" data-testid="download-row">
              <button className="leduro-btn-ghost justify-center !py-3 !px-3 text-[11px]" data-testid="download-datasheet">
                <Download className="h-4 w-4" /> Download Datasheet (PDF)
              </button>
              <button className="leduro-btn-ghost justify-center !py-3 !px-3 text-[11px]" data-testid="download-ies">
                <Download className="h-4 w-4" /> IES file
              </button>
            </div>

            {/* Trust row */}
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3 border-t border-leduro-border pt-5">
              <Trust Icon={Truck}>48h dispatch from Rīga</Trust>
              <Trust Icon={ShieldCheck}>{product.warrantyYears}Y year warranty</Trust>
              <div className="sm:col-span-2 flex items-center gap-2 text-sm text-leduro-navy">
                <Check className="h-4 w-4 text-leduro-orange" />
                {product.certifications.join(" · ")} certified
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <section className="bg-white" data-testid="product-tabs">
        <div className="leduro-container">
          <div className="border-b border-leduro-border overflow-x-auto">
            <div className="flex items-center gap-1 min-w-max">
              {TABS.map((t, i) => (
                <button
                  key={t}
                  onClick={() => setTab(i)}
                  data-testid={`tab-${t.toLowerCase().replace(/\s/g, "-")}`}
                  className={`relative px-5 py-4 text-[11px] uppercase tracking-[0.2em] font-bold transition-colors ${
                    tab === i ? "text-leduro-navy" : "text-leduro-muted hover:text-leduro-navy"
                  }`}
                >
                  {t}
                  {tab === i && <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-leduro-orange" />}
                </button>
              ))}
            </div>
          </div>

          <div className="py-10 md:py-14">
            {tab === 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-px" data-testid="spec-table">
                {SPECS.map(({ Icon, label, value }) => (
                  <div key={label} className="grid grid-cols-[20px_1fr_auto] items-center gap-4 py-4 border-b border-leduro-border">
                    <Icon className="h-4 w-4 text-leduro-orange" />
                    <span className="text-leduro-navy">{label}</span>
                    <span className="font-mono text-sm text-leduro-navy font-semibold text-right">{value}</span>
                  </div>
                ))}
              </div>
            )}

            {tab === 1 && (
              <div className="max-w-3xl space-y-4 text-leduro-muted leading-relaxed" data-testid="tab-description-content">
                <p className="text-lg text-leduro-navy">
                  {product.name} delivers {product.lumen} from {product.watt}, providing approximately{" "}
                  {product.efficacy} lm/W — among the highest efficacies in its class.
                </p>
                <p>{product.description}</p>
                <p>
                  All Leduro luminaires ship with measured photometric data (IES/LDT), constant-current drivers
                  and a {product.warranty} commercial warranty. Stock SKUs typically dispatch from Rīga within 24 hours.
                </p>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-3">
                  {product.highlights.map((h) => (
                    <li key={h} className="flex items-start gap-2 text-leduro-navy">
                      <Check className="h-4 w-4 text-leduro-orange shrink-0 mt-0.5" />
                      {h}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {tab === 2 && (
              <div data-testid="tab-applications-content">
                <p className="text-leduro-muted max-w-2xl mb-8">
                  {product.name} is engineered for the following commercial environments:
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-leduro-border border border-leduro-border">
                  {product.applications.map((app) => (
                    <div key={app} className="bg-white p-6 text-center">
                      <Settings className="h-6 w-6 text-leduro-orange mx-auto" />
                      <p className="mt-3 font-heading font-bold text-leduro-navy">{app}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {tab === 3 && (
              <div className="max-w-2xl space-y-3" data-testid="tab-downloads-content">
                {[
                  { Icon: FileText, name: "Product datasheet (PDF)", size: "2.1 MB" },
                  { Icon: FileImage, name: "IES photometric file", size: "84 KB" },
                  { Icon: FileImage, name: "LDT photometric file", size: "92 KB" },
                  { Icon: FileText, name: "Declaration of conformity (CE)", size: "320 KB" },
                  { Icon: FileText, name: "Installation manual", size: "1.4 MB" },
                ].map(({ Icon, name, size }) => (
                  <button key={name} className="w-full flex items-center justify-between gap-4 p-5 border border-leduro-border hover:bg-leduro-surface transition-colors">
                    <div className="flex items-center gap-4">
                      <Icon className="h-5 w-5 text-leduro-orange" />
                      <span className="font-bold text-leduro-navy">{name}</span>
                    </div>
                    <span className="text-xs font-mono text-leduro-muted flex items-center gap-3">
                      {size} <Download className="h-4 w-4 text-leduro-navy" />
                    </span>
                  </button>
                ))}
              </div>
            )}

            {tab === 4 && (
              <div className="max-w-3xl space-y-4 text-leduro-muted leading-relaxed" data-testid="tab-warranty-content">
                <p className="text-lg text-leduro-navy font-bold">
                  {product.warrantyYears}-year limited commercial warranty.
                </p>
                <p>
                  Leduro guarantees that {product.name} is free from defects in materials and workmanship for
                  {" "}{product.warrantyYears} years from the date of purchase, when operated within the rated
                  conditions stated on the datasheet.
                </p>
                <p>
                  Warranty claims are processed within 5 business days. Replacements ship from regional stock —
                  on-site service is not required for trade buyers. RMA forms are available on the{" "}
                  <Link to="/useful-information" className="text-leduro-orange font-semibold">Useful Information</Link>{" "}
                  page.
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="bg-leduro-surface py-16 md:py-20" data-testid="related-products">
          <div className="leduro-container">
            <div className="flex items-end justify-between mb-8">
              <div>
                <span className="leduro-eyebrow">— Related</span>
                <h2 className="mt-2 font-heading font-bold text-leduro-navy text-2xl md:text-3xl">
                  You may <span className="text-leduro-orange">also need</span>
                </h2>
              </div>
              <Link to={`/catalogue/${category?.slug}`} className="leduro-btn-ghost" data-testid="related-view-all">
                View all
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {related.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
